import { NextSeo } from "next-seo";
import {
    AppWindow,
    BarChart3,
    Box,
    Building2,
    Coins,
    ShieldCheck,
    type LucideIcon,
} from "lucide-react";
import { CitationsCarousel } from "~/modules/billing/components/CitationsCarousel";
import { Footer } from "~/components/Footer";
import { ImageLoader } from "~/components/ImageLoader";
import { Navbar } from "~/components/Navbar";
import { SidebarLayout, PageWrapper } from "~/components/SidebarLayout";
import { DefaultFormModal } from "~/components/DefaultForm";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { getButtonClassName, ButtonVariants } from "~/components/Button";
import { Routes } from "~/routes";
import {
    Fragment,
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactNode,
} from "react";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { NavigationUserEvents } from "~/modules/analytics/events/navigation";
import { queryMediaCitations } from "~/modules/media_citations/api/router";
import { MediaCitation } from "~/modules/media_citations/types";
import { queryBuidlLandingPageData } from "~/modules/platform-overview/api/getBuidlLandingPageData";
import {
    BuidlLandingPageData,
    BuidlLandingPageReferencePreview,
} from "~/modules/platform-overview/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { Link } from "~/components/Link";
import { PlatformOverviewUserEvents } from "~/modules/analytics/events/billing";
import { Anchor } from "~/components/Anchor";
import { RWA_URLS } from "~/consts";

// // // //

interface SalesPageProps {
    citations: MediaCitation[];
    buidlLandingPageData: BuidlLandingPageData;
    buidlLandingPageReferencePreview: BuidlLandingPageReferencePreview;
}

export async function getStaticProps() {
    const [
        allCitations,
        { buidlLandingPageData, buidlLandingPageReferencePreview },
    ] = await Promise.all([queryMediaCitations(), queryBuidlLandingPageData()]);

    const citations: MediaCitation[] = allCitations
        .filter((c) => c.featured === true)
        .slice(0, 20);

    const props: SalesPageProps = {
        citations,
        buidlLandingPageData,
        buidlLandingPageReferencePreview,
    };

    return {
        props,
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function SalesPage(props: SalesPageProps) {
    const {
        citations,
        buidlLandingPageData,
        buidlLandingPageReferencePreview,
    } = props;

    return (
        <PageTrack>
            <NextSeo
                canonical={addBaseUrl(Routes.salesPage)}
                title="Data & API Platform Overview"
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Data & API Platform Overview",
                        href: Routes.salesPage,
                    },
                ]}
            />

            <UserFetcher>{({ user }) => <Navbar user={user} />}</UserFetcher>
            <SidebarLayout pathname={Routes.salesPage}>
                <PageWrapper>
                    <Info
                        citations={citations}
                        buidlLandingPageData={buidlLandingPageData}
                        buidlLandingPageReferencePreview={
                            buidlLandingPageReferencePreview
                        }
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}

const solutionCards = [
    {
        title: "Market Intelligence",
        description:
            "Track issuer traction, asset flows, and tokenized product adoption across platforms, networks, and asset classes.",
        icon: BarChart3,
    },
    {
        title: "Asset Diligence",
        description:
            "Review structures, disclosures, counterparties, reserve composition, and lifecycle terms from one normalized source.",
        icon: ShieldCheck,
    },
    {
        title: "Fintech Apps",
        description:
            "Power search, alerts, portfolio views, and operational workflows with production-ready entity, asset, and token data.",
        icon: AppWindow,
    },
] as const satisfies readonly {
    title: string;
    description: string;
    icon: LucideIcon;
}[];

const developerExperienceItems = [
    {
        title: "Explore the schema and live payloads",
        description:
            "Browse entities, assets, tokens, and example responses before you write code.",
    },
    {
        title: "Build queries in a visual editor",
        description:
            "Filter, sort, group, and aggregate data with a visual query builder, then preview results instantly.",
    },
    {
        title: "Deliver results through API, CSV, Excel, or Snowflake",
        description:
            "Generate code snippets and move the same dataset into notebooks, dashboards, and production systems.",
    },
] as const;

const dataCoverageColumns = [
    {
        title: "Entities",
        description:
            "Verified profiles for issuers and service providers so teams can understand who stands behind an asset.",
        icon: Building2,
        fields: [
            "Issuer name",
            "Jurisdiction",
            "Custodian",
            "Administrator",
            "Auditor",
            "Entity IDs",
        ],
    },
    {
        title: "Assets",
        description:
            "Normalized product terms and reserve details to evaluate structure, economics, and access requirements.",
        icon: Coins,
        fields: [
            "Asset class",
            "AUM",
            "Yield",
            "Min ticket",
            "Redemptions",
            "Collateral mix",
        ],
    },
    {
        title: "Tokens",
        description:
            "Token-level metadata and network activity for monitoring, product experiences, and diligence workflows.",
        icon: Box,
        fields: [
            "Contract address",
            "Token standard",
            "Supported networks",
            "Holder count",
            "Transfers",
            "Mints / burns",
        ],
    },
] as const;

const dataCoverageMarketMetrics = [
    {
        label: "AUM",
        value: "$2.52B",
        delta: "+8.3% 30D",
        chartType: "line",
        bars: [
            38, 39, 41, 42, 44, 46, 47, 49, 51, 52, 54, 56, 58, 60, 63, 66, 69,
            72, 75, 78, 81, 85, 89, 94,
        ],
    },
    {
        label: "APY",
        value: "4.88%",
        delta: "+12 bps",
        chartType: "line",
        bars: [
            78, 80, 77, 81, 79, 82, 78, 80, 77, 81, 79, 83, 80, 78, 81, 79, 82,
            78, 80, 77, 81, 79, 82, 80,
        ],
    },
    {
        label: "Fund Flows",
        value: "132 / 118",
        delta: "Last 30D events",
        chartType: "diverging",
        bars: [
            34, 42, 29, 47, 38, 51, 33, 56, 41, 49, 36, 58, 44, 54, 39, 61, 46,
            57, 43, 64, 48, 59, 52, 62,
        ],
        secondaryBars: [
            31, 39, 27, 44, 35, 47, 30, 52, 39, 45, 33, 55, 41, 50, 37, 58, 44,
            53, 40, 61, 46, 56, 49, 59,
        ],
    },
    {
        label: "Investor Count",
        value: "62",
        delta: "+9 wallets",
        chartType: "line",
        bars: [
            7, 8, 9, 10, 11, 12, 14, 15, 17, 18, 20, 22, 24, 27, 30, 33, 36, 39,
            43, 47, 51, 55, 59, 62,
        ],
    },
] as const;

const compactDollarFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
});

const wholeNumberFormatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
});

function renderJsonPreview(
    value: Record<string, any>,
    indentLevel = 0,
): ReactNode {
    const indent = "  ".repeat(indentLevel);
    const childIndent = "  ".repeat(indentLevel + 1);

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return <span className="text-slate-500">[]</span>;
        }

        return (
            <>
                <span className="text-slate-500">[</span>
                {"\n"}
                {value.map((item, index) => (
                    <Fragment key={`${indentLevel}-${index}`}>
                        {childIndent}
                        {renderJsonPreview(item, indentLevel + 1)}
                        {index < value.length - 1 ? (
                            <span className="text-slate-500">,</span>
                        ) : null}
                        {"\n"}
                    </Fragment>
                ))}
                {indent}
                <span className="text-slate-500">]</span>
            </>
        );
    }

    if (value === null) {
        return <span className="text-amber-500">null</span>;
    }

    if (typeof value === "string") {
        return (
            <span className="text-emerald-600">
                {'"'}
                {value}
                {'"'}
            </span>
        );
    }

    if (typeof value === "number") {
        return <span className="text-violet-600">{value}</span>;
    }

    if (typeof value === "boolean") {
        return <span className="text-sky-600">{String(value)}</span>;
    }

    const entries = Object.entries(value);

    if (entries.length === 0) {
        return <span className="text-slate-500">{`{}`}</span>;
    }

    return (
        <>
            <span className="text-slate-500">{"{"}</span>
            {"\n"}
            {entries.map(([key, item], index) => (
                <Fragment key={key}>
                    {childIndent}
                    <span className="text-slate-700">
                        {'"'}
                        {key}
                        {'"'}
                    </span>
                    <span className="text-slate-500">: </span>
                    {renderJsonPreview(item, indentLevel + 1)}
                    {index < entries.length - 1 ? (
                        <span className="text-slate-500">,</span>
                    ) : null}
                    {"\n"}
                </Fragment>
            ))}
            {indent}
            <span className="text-slate-500">{"}"}</span>
        </>
    );
}

function renderRequestPreviewLines(): ReactNode[] {
    return [
        <>
            <span className="text-violet-600">curl</span>
            <span className="text-sky-600"> -G</span>
            <span className="text-slate-700"> </span>
            <span className="text-emerald-600">
                {'"https://api.rwa.xyz/v4/assets"'}
            </span>
            <span className="text-slate-500"> \</span>
        </>,
        <>
            <span className="text-sky-600"> -H</span>
            <span className="text-slate-700"> </span>
            <span className="text-emerald-600">
                {'"Authorization: Bearer $RWA_API_KEY"'}
            </span>
            <span className="text-slate-500"> \</span>
        </>,
        <>
            <span className="text-sky-600"> --data-urlencode</span>
            <span className="text-slate-700"> </span>
            <span className="text-emerald-600">
                {
                    '\'query={"filter":{"operator":"equals","field":"ticker","value":"BUIDL"},"pagination":{"page":1,"perPage":1}}\''
                }
            </span>
        </>,
    ];
}

function Info(props: SalesPageProps) {
    const {
        citations,
        buidlLandingPageData,
        buidlLandingPageReferencePreview,
    } = props;

    return (
        <div className="mx-auto mt-4 w-full max-w-6xl px-4 py-2 space-y-24 sm:px-6 lg:px-10 lg:space-y-28">
            <HeroSection buidlLandingPageData={buidlLandingPageData} />
            <CitationsCarousel citations={citations} className="mt-0" />
            <UseCases />
            <DataCoverageIntroSection />
            <DataCoverageSection
                buidlLandingPageReferencePreview={
                    buidlLandingPageReferencePreview
                }
            />
            <DeveloperExperienceSection />
            <FinalCTASection />
        </div>
    );
}

function HeroSection(props: { buidlLandingPageData: BuidlLandingPageData }) {
    const { buidlLandingPageData } = props;

    return (
        <section className="relative overflow-hidden py-10 lg:py-14">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(420px,560px)] lg:items-center lg:gap-12">
                <div className="max-w-[38rem]">
                    <div className="text-sm font-semibold tracking-normal text-slate-500 uppercase">
                        Platform Overview
                    </div>
                    <h1 className="mt-4 max-w-2xl text-4xl leading-[1.04] font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-[3.5rem]">
                        The complete data API for tokenized assets
                    </h1>
                    <p className="mt-5 max-w-[34rem] text-base leading-normal font-light text-slate-600 sm:text-lg">
                        RWA.xyz provides the only verified API for tokenized
                        securities, powered by a network of 100+ asset managers
                        and issuers directly contributing their data.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <DefaultFormModal
                            title="Request a Demo"
                            body="Share a few details about your team and use case, and we will follow up with a tailored walkthrough of the RWA.xyz data API."
                            buttonText="Request a Demo"
                            buttonClassName={getButtonClassName({
                                variant: ButtonVariants.primary,
                                size: "sm",
                                rounded: "md",
                                className: "cursor-pointer",
                            })}
                            analyticsLabel={
                                NavigationUserEvents.navbarRequestDemoClick
                            }
                        />
                        <div className="flex basis-full items-center gap-3 sm:basis-auto">
                            <Link
                                href={Routes.catalog}
                                analyticsLabel={
                                    PlatformOverviewUserEvents.ViewDataCatalogHeroLinkClick
                                }
                                className={getButtonClassName({
                                    variant: ButtonVariants.primaryOutline,
                                    size: "sm",
                                    rounded: "md",
                                })}
                            >
                                View Data Catalog
                            </Link>
                            <Anchor
                                openInNewTab
                                href={RWA_URLS.docs}
                                analyticsLabel={
                                    PlatformOverviewUserEvents.ViewDocumentationHeroLinkClick
                                }
                                className={getButtonClassName({
                                    variant: ButtonVariants.primaryLink,
                                    size: "sm",
                                })}
                            >
                                Documentation
                            </Anchor>
                        </div>
                    </div>
                </div>

                <div className="relative w-full lg:justify-self-end">
                    <div className="relative flex justify-center lg:justify-end">
                        <AssetPreviewCard
                            buidlLandingPageData={buidlLandingPageData}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

function AssetPreviewCard(props: {
    buidlLandingPageData: BuidlLandingPageData;
}) {
    const { buidlLandingPageData } = props;
    const requestRef = useRef<HTMLDivElement>(null);
    const [requestOffset, setRequestOffset] = useState(0);
    const [shiftAmount, setShiftAmount] = useState<number | null>(null);
    const hasShifted = shiftAmount !== null;

    // QUESTION - wtf is THIS doing???
    useEffect(() => {
        const element = requestRef.current;

        if (!element) {
            return;
        }

        const updateRequestOffset = () => {
            setRequestOffset(element.offsetHeight);
        };

        updateRequestOffset();

        const resizeObserver = new ResizeObserver(() => {
            updateRequestOffset();
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // QUESTION - wtf is THIS doing???
    useEffect(() => {
        if (requestOffset <= 0 || shiftAmount !== null) {
            return;
        }

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setShiftAmount(requestOffset);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setShiftAmount(requestOffset);
        }, 2000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [requestOffset, shiftAmount]);

    return (
        <div className="w-full max-w-[450px]">
            <div
                className="relative h-[550px] overflow-hidden rounded-md border border-slate-500/20 bg-slate-100 shadow-xs ring-1 ring-gray-900/5"
                style={
                    {
                        "--platform-overview-request-offset": `${requestOffset}px`,
                    } as CSSProperties
                }
            >
                <div
                    className="platform-overview-preview-track flex flex-col"
                    style={{
                        transform: `translateY(-${shiftAmount ?? 0}px)`,
                        transition:
                            requestOffset > 0
                                ? "transform 950ms cubic-bezier(0.79, 0.14, 0.15, 0.86)"
                                : undefined,
                        willChange: "transform",
                    }}
                >
                    <div
                        ref={requestRef}
                        className="platform-overview-preview-request-panel"
                        style={{
                            opacity: hasShifted ? 0 : 1,
                            transition:
                                "opacity 250ms cubic-bezier(0.79, 0.14, 0.15, 0.86) 120ms",
                        }}
                    >
                        <CurlRequestPreview />
                    </div>
                    <div
                        className="platform-overview-preview-content-panel"
                        style={{
                            opacity: hasShifted ? 1 : 0,
                            transition:
                                "opacity 500ms cubic-bezier(0.79, 0.14, 0.15, 0.86) 120ms",
                        }}
                    >
                        <AssetResponsePreview
                            buidlLandingPageData={buidlLandingPageData}
                        />
                    </div>
                </div>
            </div>

            <style jsx>{`
                :global(.platform-overview-preview-request-panel) {
                    flex: none;
                }

                :global(.platform-overview-preview-cursor) {
                    animation: platform-overview-preview-cursor-blink 1s
                        step-end infinite;
                }

                :global(.platform-overview-request-line) {
                    opacity: 0;
                    transform: translateY(-8px);
                    animation: platform-overview-request-line-enter 320ms ease
                        forwards;
                }

                :global(.platform-overview-preview-content-panel) {
                    flex: none;
                }

                :global(.platform-overview-code-scroll) {
                    scrollbar-width: thin;
                    scrollbar-color: transparent transparent;
                }

                :global(.platform-overview-code-scroll::-webkit-scrollbar) {
                    width: 8px;
                    height: 8px;
                    background: transparent;
                }

                :global(
                    .platform-overview-code-scroll::-webkit-scrollbar-thumb
                ) {
                    background: transparent;
                }

                @keyframes platform-overview-preview-cursor-blink {
                    0%,
                    49% {
                        opacity: 1;
                    }

                    50%,
                    100% {
                        opacity: 0;
                    }
                }

                @keyframes platform-overview-request-line-enter {
                    from {
                        opacity: 0;
                        transform: translateY(-8px);
                    }

                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    :global(.platform-overview-request-line) {
                        animation: none;
                        opacity: 1;
                        transform: translateY(0);
                    }

                    :global(.platform-overview-preview-cursor) {
                        animation: none;
                    }
                }
            `}</style>
        </div>
    );
}

function CurlRequestPreview() {
    return (
        <div className="shrink-0 bg-slate-100 p-4 pb-3 text-slate-800">
            <div className="flex items-center justify-between gap-4">
                <div className="text-[11px] border border-slate-300 rounded-md px-2.5 py-1 font-mono font-semibold uppercase tracking-wider text-slate-500">
                    API Request
                </div>
                <div className="rounded-md border border-slate-300 bg-card px-2.5 py-1 text-[11px] text-slate-500">
                    curl
                </div>
            </div>

            <div className="mt-4">
                <div className="rounded-md p-4">
                    <pre className="platform-overview-code-scroll overflow-auto whitespace-pre-wrap break-all text-[11px] leading-5 text-slate-700">
                        <code>
                            {renderRequestPreviewLines().map(
                                (line, index, lines) => (
                                    <span
                                        key={index}
                                        className="platform-overview-request-line block"
                                        style={{
                                            animationDelay: `${index * 120}ms`,
                                        }}
                                    >
                                        {line}
                                        {index === lines.length - 1 ? (
                                            <span className="platform-overview-preview-cursor ml-1 inline-block h-4 w-[7px] translate-y-[2px] bg-slate-500 align-middle" />
                                        ) : null}
                                    </span>
                                ),
                            )}
                        </code>
                    </pre>
                </div>
            </div>
        </div>
    );
}

function AssetResponsePreview(props: {
    buidlLandingPageData: BuidlLandingPageData;
}) {
    const { buidlLandingPageData } = props;

    return (
        <div className="flex h-[550px] flex-col bg-slate-100">
            <div className="m-3 mb-1 shrink-0 rounded-md border border-slate-300 bg-card p-4">
                <div className="min-h-[220px]">
                    <AssetSnapshotCardA
                        buidlLandingPageData={buidlLandingPageData}
                    />
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col bg-slate-100 p-4 text-slate-800">
                <div className="text-[11px] font-mono font-semibold uppercase tracking-wider text-slate-500">
                    Response
                </div>

                <pre className="platform-overview-code-scroll min-h-0 flex-1 overflow-auto rounded-md p-3 text-xs leading-4 text-slate-700">
                    <code>{renderJsonPreview(buidlLandingPageData)}</code>
                </pre>
            </div>
        </div>
    );
}

const EMPTY_PREVIEW_VALUE = "—";

function formatCompactDollars(value: number | null): string {
    return value === null
        ? EMPTY_PREVIEW_VALUE
        : compactDollarFormatter.format(value);
}

function AssetSnapshotCardA(props: {
    buidlLandingPageData: BuidlLandingPageData;
}) {
    const { buidlLandingPageData } = props;
    const { apy7DayPercent, holders } = buidlLandingPageData;

    const badgeLabels = [
        buidlLandingPageData.assetClass,
        buidlLandingPageData.primaryNetwork,
        buidlLandingPageData.redemptionFrequency,
        buidlLandingPageData.investorType,
    ].filter((label): label is string => label !== null);

    return (
        <div className="flex h-full flex-col">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="text-[1.6rem] leading-none font-semibold tracking-tight text-foreground">
                            {buidlLandingPageData.slug}
                        </div>
                        <div className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-medium tracking-wide text-blue-700">
                            Issuer-Contributed
                        </div>
                    </div>
                    <div className="mt-2 line-clamp-2 text-sm font-light text-slate-500">
                        {buidlLandingPageData.name}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        AUM
                    </div>
                    <div className="mt-1 text-2xl leading-none font-semibold tracking-tight text-foreground">
                        {formatCompactDollars(buidlLandingPageData.aumUsd)}
                    </div>
                    <div className="mt-2 text-xs font-light text-slate-500">
                        {buidlLandingPageData.supportedNetworks.length} networks
                        live
                    </div>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2.5">
                <PreviewMetricTile
                    label="7D APY"
                    value={
                        apy7DayPercent === null
                            ? EMPTY_PREVIEW_VALUE
                            : apy7DayPercent.toFixed(2)
                    }
                    suffix={apy7DayPercent === null ? undefined : "%"}
                />
                <PreviewMetricTile
                    label="Min Ticket"
                    value={formatCompactDollars(
                        buidlLandingPageData.minimumSubscriptionUsd,
                    )}
                />
                <PreviewMetricTile
                    label="Holders"
                    value={
                        holders === null
                            ? EMPTY_PREVIEW_VALUE
                            : wholeNumberFormatter.format(holders)
                    }
                />
            </div>

            <div className="mt-4 h-px bg-slate-200" />

            <div className="mt-3 grid grid-cols-3 gap-3">
                <PreviewSummary
                    label="Issuer"
                    value={buidlLandingPageData.issuer ?? EMPTY_PREVIEW_VALUE}
                />
                <PreviewSummary
                    label="Platform"
                    value={buidlLandingPageData.platform ?? EMPTY_PREVIEW_VALUE}
                />
                <PreviewSummary
                    label="Custodian"
                    value={
                        buidlLandingPageData.serviceProviders.custodian ??
                        EMPTY_PREVIEW_VALUE
                    }
                />
            </div>

            {badgeLabels.length > 0 ? (
                <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                    {badgeLabels.map((label) => (
                        <PreviewBadge key={label} label={label} />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function PreviewBarChart(props: {
    bars: readonly number[];
    secondaryBars?: readonly number[];
    chartType?: "standard" | "diverging";
    animate?: boolean;
}) {
    const {
        bars,
        secondaryBars,
        chartType = "standard",
        animate = true,
    } = props;
    const max = Math.max(...bars, ...(secondaryBars ?? [0]));
    const animationPlayState = animate ? "running" : "paused";

    if (chartType === "diverging" && secondaryBars) {
        return (
            <div className="relative flex h-28 items-stretch gap-1">
                <div className="absolute inset-x-0 top-1/2 h-px -translate-y-px bg-slate-200" />

                {bars.map((value, index) => {
                    const positiveHeight = Math.max((value / max) * 100, 8);
                    const negativeValue = secondaryBars[index] ?? 0;
                    const negativeHeight = Math.max(
                        (negativeValue / max) * 100,
                        6,
                    );

                    return (
                        <div
                            key={`${value}-${negativeValue}-${index}`}
                            className="flex min-w-0 flex-1 flex-col"
                        >
                            <div className="flex h-1/2 items-end pb-1">
                                <div
                                    className="platform-overview-bar-chart-bar-positive w-full rounded-t-[2px] bg-blue-600"
                                    style={{
                                        height: `${positiveHeight}%`,
                                        animationDelay: `${index * 28}ms`,
                                        animationPlayState,
                                    }}
                                />
                            </div>
                            <div className="flex h-1/2 items-start pt-1">
                                <div
                                    className="platform-overview-bar-chart-bar-negative w-full rounded-b-[2px] bg-blue-200"
                                    style={{
                                        height: `${negativeHeight}%`,
                                        animationDelay: `${index * 28 + 24}ms`,
                                        animationPlayState,
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}

                <style jsx>{`
                    :global(.platform-overview-bar-chart-bar-positive) {
                        transform-origin: bottom;
                        transform: scaleY(0);
                        animation: platform-overview-bar-chart-grow 700ms
                            cubic-bezier(0.22, 1, 0.36, 1) forwards;
                    }

                    :global(.platform-overview-bar-chart-bar-negative) {
                        transform-origin: top;
                        transform: scaleY(0);
                        animation: platform-overview-bar-chart-grow 700ms
                            cubic-bezier(0.22, 1, 0.36, 1) forwards;
                    }

                    @keyframes platform-overview-bar-chart-grow {
                        from {
                            transform: scaleY(0);
                        }

                        to {
                            transform: scaleY(1);
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        :global(.platform-overview-bar-chart-bar-positive),
                        :global(.platform-overview-bar-chart-bar-negative) {
                            animation: none;
                            transform: scaleY(1);
                        }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="flex h-20 items-end gap-1">
            {bars.map((value, index) => {
                const primaryHeight = Math.max((value / max) * 100, 10);
                const secondaryValue = secondaryBars?.[index];
                const secondaryHeight =
                    secondaryValue !== undefined
                        ? Math.max((secondaryValue / max) * 100, 8)
                        : 0;

                return (
                    <div
                        key={`${value}-${index}`}
                        className="flex h-full min-w-0 flex-1 items-end gap-1"
                    >
                        <div
                            className="platform-overview-bar-chart-bar w-full rounded-t-[2px] bg-slate-500"
                            style={{
                                height: `${primaryHeight}%`,
                                animationDelay: `${index * 45}ms`,
                                animationPlayState,
                            }}
                        />
                        {secondaryValue !== undefined ? (
                            <div
                                className="platform-overview-bar-chart-bar w-full rounded-t-[2px] bg-slate-300"
                                style={{
                                    height: `${secondaryHeight}%`,
                                    animationDelay: `${index * 45 + 40}ms`,
                                    animationPlayState,
                                }}
                            />
                        ) : null}
                    </div>
                );
            })}

            <style jsx>{`
                :global(.platform-overview-bar-chart-bar) {
                    transform-origin: bottom;
                    transform: scaleY(0);
                    animation: platform-overview-bar-chart-grow 700ms
                        cubic-bezier(0.22, 1, 0.36, 1) forwards;
                }

                @keyframes platform-overview-bar-chart-grow {
                    from {
                        transform: scaleY(0);
                    }

                    to {
                        transform: scaleY(1);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    :global(.platform-overview-bar-chart-bar) {
                        animation: none;
                        transform: scaleY(1);
                    }
                }
            `}</style>
        </div>
    );
}

function PreviewLineChart(props: {
    values: readonly number[];
    animate?: boolean;
}) {
    const { values, animate = true } = props;

    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const animationPlayState = animate ? "running" : "paused";
    const chartTop = 24;
    const chartBottom = 52;
    const chartHeight = chartBottom - chartTop;
    const paddedMin = min - range * 0.35;
    const paddedMax = max + range * 0.35;
    const paddedRange = paddedMax - paddedMin || 1;

    const points = values
        .map((value, index) => {
            const x = (index / (values.length - 1)) * 160;
            const y =
                chartBottom - ((value - paddedMin) / paddedRange) * chartHeight;

            return `${x},${y}`;
        })
        .join(" ");

    return (
        <svg
            viewBox="0 0 160 72"
            preserveAspectRatio="none"
            className="h-20 w-full overflow-visible"
            aria-hidden="true"
        >
            <line
                x1="0"
                y1="16"
                x2="160"
                y2="16"
                stroke="rgba(169, 172, 182, 0.24)"
                strokeDasharray="3 4"
            />
            <line
                x1="0"
                y1="40"
                x2="160"
                y2="40"
                stroke="rgba(169, 172, 182, 0.24)"
                strokeDasharray="3 4"
            />
            <line
                x1="0"
                y1="64"
                x2="160"
                y2="64"
                stroke="rgba(169, 172, 182, 0.24)"
            />

            <polyline
                fill="none"
                stroke="rgba(49, 108, 205, 0.95)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={points}
                className="platform-overview-line-chart-line"
                style={{ animationPlayState }}
            />

            {values.map((value, index) => {
                const x = (index / (values.length - 1)) * 160;
                const y =
                    chartBottom -
                    ((value - paddedMin) / paddedRange) * chartHeight;

                return (
                    <circle
                        key={`${value}-${index}`}
                        cx={x}
                        cy={y}
                        r="2.5"
                        fill="white"
                        stroke="rgba(49, 108, 205, 0.95)"
                        strokeWidth="1.5"
                        className="platform-overview-line-chart-dot"
                        style={{
                            animationDelay: `${index * 28 + 160}ms`,
                            animationPlayState,
                        }}
                    />
                );
            })}

            <style jsx>{`
                :global(.platform-overview-line-chart-line) {
                    stroke-dasharray: 260;
                    stroke-dashoffset: 260;
                    animation: platform-overview-line-chart-draw 900ms
                        cubic-bezier(0.22, 1, 0.36, 1) forwards;
                }

                :global(.platform-overview-line-chart-dot) {
                    opacity: 0;
                    transform-origin: center;
                    animation: platform-overview-line-chart-dot-in 260ms
                        ease-out forwards;
                }

                @keyframes platform-overview-line-chart-draw {
                    to {
                        stroke-dashoffset: 0;
                    }
                }

                @keyframes platform-overview-line-chart-dot-in {
                    from {
                        opacity: 0;
                        transform: scale(0.6);
                    }

                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    :global(.platform-overview-line-chart-line) {
                        animation: none;
                        stroke-dashoffset: 0;
                    }

                    :global(.platform-overview-line-chart-dot) {
                        animation: none;
                        opacity: 1;
                        transform: none;
                    }
                }
            `}</style>
        </svg>
    );
}

function useAnimateOnFirstView<T extends Element>() {
    const ref = useRef<T | null>(null);
    const [hasEnteredViewport, setHasEnteredViewport] = useState(false);

    useEffect(() => {
        if (hasEnteredViewport) {
            return;
        }

        const node = ref.current;

        if (!node || typeof IntersectionObserver === "undefined") {
            setHasEnteredViewport(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];

                if (entry?.isIntersecting) {
                    setHasEnteredViewport(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.35,
                rootMargin: "0px 0px -8% 0px",
            },
        );

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [hasEnteredViewport]);

    return { ref, hasEnteredViewport };
}

function PreviewMetricTile(props: {
    label: string;
    value: string;
    prefix?: string;
    suffix?: string;
}) {
    const { label, value, prefix, suffix } = props;

    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                {label}
            </div>
            <div className="mt-1 truncate text-base leading-none font-semibold tracking-tight text-foreground">
                {prefix}
                {value}
                {suffix}
            </div>
        </div>
    );
}

function PreviewBadge(props: { label: string }) {
    const { label } = props;

    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
            {label}
        </div>
    );
}

function PreviewSummary(props: { label: string; value: string }) {
    const { label, value } = props;

    return (
        <div className="min-w-0">
            <div className="truncate text-sm font-medium tracking-tight text-foreground">
                {value}
            </div>
            <div className="mt-0.5 text-xs font-light text-slate-400">
                {label}
            </div>
        </div>
    );
}

function UseCases() {
    return (
        <section>
            <div className="mb-4 mt-8 inline-flex rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                Use Cases
            </div>

            <div className="max-w-5xl">
                <p className="text-3xl leading-[1.12] font-semibold tracking-tight sm:text-4xl">
                    <span className="text-slate-900">
                        The security master for tokenized finance is here.{" "}
                    </span>
                    <span className="text-slate-500">
                        Purpose-built to power market intelligence, asset
                        diligence, and fintech apps.
                    </span>
                </p>
            </div>

            <div className="mt-14 grid gap-8 lg:grid-cols-3 lg:gap-0">
                {solutionCards.map((card, index) => (
                    <UseCaseColumn
                        key={card.title}
                        title={card.title}
                        description={card.description}
                        icon={card.icon}
                        index={index}
                    />
                ))}
            </div>
        </section>
    );
}

function UseCaseColumn(props: {
    title: string;
    description: string;
    icon: LucideIcon;
    index: number;
}) {
    const { title, description, icon: Icon, index } = props;

    return (
        <div
            className={[
                "flex flex-col lg:min-h-[18rem] lg:border-t-0 lg:pt-0",
                index === 0
                    ? "pt-0 lg:pr-8"
                    : "border-t border-dashed border-slate-300 pt-8",
                index === 1
                    ? "lg:border-l lg:border-r lg:border-dashed lg:border-slate-300 lg:px-8"
                    : "",
                index === 2 ? "lg:pl-8" : "",
            ].join(" ")}
        >
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-slate-100 text-slate-700">
                <Icon className="h-4 w-4" strokeWidth={2} />
            </div>

            <div className="mt-7 text-2xl leading-[1.16] font-semibold tracking-tight text-slate-900">
                {title}
            </div>
            <div className="mt-3 max-w-[19rem] text-base leading-[1.55] font-light text-slate-600">
                {description}
            </div>
        </div>
    );
}

function DataCoverageSection(props: {
    buidlLandingPageReferencePreview: BuidlLandingPageReferencePreview;
}) {
    const { buidlLandingPageReferencePreview } = props;

    return (
        <section>
            <div className="mb-4 inline-flex rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                Data Model
            </div>

            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:items-start lg:gap-12">
                <div className="max-w-4xl">
                    <p className="text-3xl leading-[1.12] font-semibold tracking-tight sm:text-4xl">
                        <span className="text-slate-900">
                            Coverage across entities, assets, and tokens.{" "}
                        </span>
                        <span className="text-slate-500">
                            Structured for research, product, and compliance
                            teams.
                        </span>
                    </p>
                </div>

                <div className="max-w-2xl text-base leading-[1.55] font-light text-slate-600 sm:text-lg">
                    From issuer profiles and reserve details to token contracts
                    and onchain activity, the API maps the full object model
                    behind tokenized securities.
                </div>
            </div>

            <div className="mt-[4.5rem] grid gap-8 lg:grid-cols-3 lg:gap-0">
                {dataCoverageColumns.map((column, index) => (
                    <DataCoverageColumn
                        key={column.title}
                        title={column.title}
                        description={column.description}
                        icon={column.icon}
                        fields={column.fields}
                        index={index}
                    />
                ))}
            </div>

            <div className="mt-20 grid gap-8 lg:grid-cols-2 lg:gap-12">
                <div>
                    <div className="mb-4 text-2xl leading-[1.12] font-semibold tracking-tight text-slate-900 sm:text-3xl">
                        Go broad with market data.
                    </div>
                    <MarketDataPanel />
                </div>

                <div>
                    <div className="mb-4 text-2xl leading-[1.12] font-semibold tracking-tight text-slate-900 sm:text-3xl">
                        Go deep with reference data.
                    </div>
                    <ReferenceDataPanel
                        buidlLandingPageReferencePreview={
                            buidlLandingPageReferencePreview
                        }
                    />
                </div>
            </div>
        </section>
    );
}

function DataCoverageIntroSection() {
    return (
        <section>
            <div className="grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(420px,1.1fr)] lg:items-center lg:gap-12">
                <div className="max-w-xl">
                    <div className="mb-4 inline-flex rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                        Data Contributors
                    </div>

                    <h2 className="text-3xl leading-[1.08] font-semibold tracking-tight text-slate-900 sm:text-4xl">
                        A unified view, built on an ecosystem of issuers and
                        platforms
                    </h2>

                    <p className="mt-5 text-base leading-[1.55] font-light text-slate-600 sm:text-lg">
                        Direct relationships with issuers and platforms drive
                        broader, timelier, and more accurate coverage.
                    </p>

                    <div className="mt-8 space-y-5">
                        <div>
                            <div className="text-lg leading-[1.18] font-semibold tracking-tight text-slate-900">
                                Structured diligence
                            </div>
                            <div className="mt-1.5 text-sm leading-normal font-light text-slate-600">
                                Every issuer goes through the same onboarding
                                process.
                            </div>
                        </div>

                        <div>
                            <div className="text-lg leading-[1.18] font-semibold tracking-tight text-slate-900">
                                Direct communication
                            </div>
                            <div className="mt-1.5 text-sm leading-normal font-light text-slate-600">
                                We work directly with issuers and platforms to
                                verify updates and resolve edge cases.
                            </div>
                        </div>

                        <div>
                            <div className="text-lg leading-[1.18] font-semibold tracking-tight text-slate-900">
                                Ongoing maintenance
                            </div>
                            <div className="mt-1.5 text-sm leading-normal font-light text-slate-600">
                                Records are maintained as products launch,
                                evolve, and update terms across issuers and
                                platforms.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mx-auto w-full max-w-4xl lg:mx-0 lg:ml-auto">
                    <ImageLoader
                        src="/platform-overview/group-6429.svg"
                        alt="Diagram showing multiple issuers flowing data into a single aggregated node."
                        className="h-auto w-full select-none"
                    />
                </div>
            </div>
        </section>
    );
}

function DataCoverageColumn(props: {
    title: string;
    description: string;
    icon: LucideIcon;
    fields: readonly string[];
    index: number;
}) {
    const { title, description, icon: Icon, fields, index } = props;

    return (
        <div
            className={[
                "flex flex-col lg:min-h-[20rem] lg:border-t-0 lg:pt-0",
                index === 0
                    ? "pt-0 lg:pr-8"
                    : "border-t border-dashed border-slate-300 pt-8",
                index === 1
                    ? "lg:border-l lg:border-r lg:border-dashed lg:border-slate-300 lg:px-8"
                    : "",
                index === 2 ? "lg:pl-8" : "",
            ].join(" ")}
        >
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-slate-100 text-slate-700">
                <Icon className="h-4 w-4" strokeWidth={2} />
            </div>

            <div className="mt-6 text-2xl leading-[1.16] font-semibold tracking-tight text-slate-900">
                {title}
            </div>

            <div className="mt-3 max-w-[20rem] text-base leading-[1.55] font-light text-slate-600">
                {description}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
                {fields.map((field) => (
                    <div
                        key={field}
                        className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
                    >
                        {field}
                    </div>
                ))}
            </div>
        </div>
    );
}

function MarketDataPanel() {
    return (
        <div className="rounded-md border border-slate-300 bg-transparent p-5 sm:p-6">
            <div className="inline-flex rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                Market Data
            </div>

            <div className="mt-5 max-w-xl">
                <div className="text-2xl leading-[1.12] font-semibold tracking-tight text-slate-900">
                    Historical market data for every trend, from AUM to
                    liquidity to rates.
                </div>
                <div className="mt-3 text-base leading-[1.55] font-light text-slate-600">
                    Track AUM, yield, holders, and activity with consistent
                    market data that can power dashboards, alerts, and market
                    intelligence products.
                </div>
            </div>

            <div className="mt-7 grid gap-x-6 gap-y-6 md:grid-cols-2">
                {dataCoverageMarketMetrics.map((metric) => (
                    <MarketMetricCard
                        key={metric.label}
                        label={metric.label}
                        bars={metric.bars}
                        chartType={
                            "chartType" in metric ? metric.chartType : undefined
                        }
                        secondaryBars={
                            "secondaryBars" in metric
                                ? metric.secondaryBars
                                : undefined
                        }
                    />
                ))}
            </div>
        </div>
    );
}

function MarketMetricCard(props: {
    label: string;
    bars: readonly number[];
    chartType?: "standard" | "diverging" | "line";
    secondaryBars?: readonly number[];
}) {
    const { label, bars, chartType, secondaryBars } = props;
    const { ref, hasEnteredViewport } = useAnimateOnFirstView<HTMLDivElement>();

    return (
        <div
            ref={ref}
            className="rounded-md border border-slate-300 bg-transparent p-3 sm:p-3.5"
        >
            <div>
                <div className="mt-1 text-[0.95rem] leading-none font-semibold tracking-tight text-slate-900">
                    {label}
                </div>
            </div>

            {secondaryBars ? (
                <div className="mt-2 flex items-center gap-3 text-[11px] font-light text-slate-500">
                    <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-blue-600" />
                        <span>Subscriptions</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-blue-200" />
                        <span>Redemptions</span>
                    </div>
                </div>
            ) : null}

            <div className="mt-2 rounded-md bg-transparent px-0 py-1">
                {chartType === "line" ? (
                    <PreviewLineChart
                        values={bars}
                        animate={hasEnteredViewport}
                    />
                ) : (
                    <PreviewBarChart
                        bars={bars}
                        chartType={
                            chartType === "diverging" ? "diverging" : "standard"
                        }
                        secondaryBars={secondaryBars}
                        animate={hasEnteredViewport}
                    />
                )}
            </div>

            <div className="mt-2 flex justify-end">
                <div className="rounded-md border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-mono text-[9px] font-semibold tracking-[0.06em] text-blue-700 uppercase">
                    GET /timeseries
                </div>
            </div>
        </div>
    );
}

function ReferenceDataPanel(props: {
    buidlLandingPageReferencePreview: BuidlLandingPageReferencePreview;
}) {
    const { buidlLandingPageReferencePreview } = props;

    return (
        <div className="rounded-md border border-slate-300 bg-transparent p-5 sm:p-6">
            <div className="inline-flex rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                Reference Data
            </div>

            <div className="mt-5 max-w-xl">
                <div className="text-2xl leading-[1.12] font-semibold tracking-tight text-slate-900">
                    Verified reference data for every market participant and
                    asset.
                </div>
                <div className="mt-3 text-base leading-[1.55] font-light text-slate-600">
                    Access the canonical object model behind each tokenized
                    security, from issuer records and service providers to
                    subscription terms and token-level identifiers.
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
                <PreviewBadge label="Entity profile" />
                <PreviewBadge label="Legal terms" />
                <PreviewBadge label="Service providers" />
                <PreviewBadge label="Token metadata" />
            </div>

            <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4">
                <pre className="platform-overview-code-scroll overflow-auto text-xs leading-5 text-slate-700">
                    <code>
                        {renderJsonPreview(buidlLandingPageReferencePreview)}
                    </code>
                </pre>
            </div>
        </div>
    );
}

function DeveloperExperienceSection() {
    return (
        <section>
            <div className="mb-4 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                <span>Developer Experience</span>
                <span className="platform-overview-developer-cursor inline-block h-[14px] w-[7px] rounded-[1px] bg-slate-500/70" />
                <span className="sr-only">Code cursor indicator</span>
            </div>

            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:items-start lg:gap-12">
                <div className="w-full max-w-none">
                    <p className="text-3xl leading-[1.12] font-semibold tracking-tight sm:text-4xl">
                        <span className="text-slate-900">
                            Explore every data point and make any query with our
                            developer platform
                        </span>
                    </p>
                </div>
            </div>

            <div className="mt-14 grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-center lg:gap-12">
                <div className="min-w-0">
                    <ImageLoader
                        src="/platform-overview/group-6425.png"
                        alt="RWA.xyz API tools interface showing asset endpoint filters and structured JSON output."
                        className="w-full h-auto select-none"
                    />
                </div>

                <div className="space-y-10">
                    <div>
                        <div className="space-y-5">
                            {developerExperienceItems.map((item, index) => (
                                <div
                                    key={item.title}
                                    className={[
                                        "pt-4",
                                        index === 0
                                            ? ""
                                            : "border-t border-slate-200",
                                    ].join(" ")}
                                >
                                    <div className="text-lg leading-[1.18] font-semibold tracking-tight text-slate-900">
                                        {item.title}
                                    </div>
                                    <div className="mt-2 text-sm leading-normal font-light text-slate-600">
                                        {item.description}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                :global(.platform-overview-developer-cursor) {
                    animation: platform-overview-developer-cursor-blink 1.2s
                        step-end infinite;
                }

                @keyframes platform-overview-developer-cursor-blink {
                    0%,
                    100% {
                        opacity: 1;
                    }

                    50%,
                    99% {
                        opacity: 0;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    :global(.platform-overview-developer-cursor) {
                        animation: none;
                    }
                }
            `}</style>
        </section>
    );
}

function FinalCTASection() {
    return (
        <section className="pt-12 pb-20 sm:pt-16 sm:pb-28">
            <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-4xl leading-[1.04] font-semibold tracking-tight text-slate-900 sm:text-5xl">
                    Query and deliver tokenized asset data.
                    <br />
                    Start building today.
                </h2>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <DefaultFormModal
                        title="Request a Demo"
                        body="Share a few details about your team and use case, and we will follow up with a tailored walkthrough of the RWA.xyz data API."
                        buttonText="Request a Demo"
                        buttonClassName={getButtonClassName({
                            variant: ButtonVariants.primary,
                            size: "md",
                            rounded: "md",
                            className: "cursor-pointer",
                        })}
                        analyticsLabel={
                            NavigationUserEvents.navbarRequestDemoClick
                        }
                    />
                    <Link
                        href={Routes.catalog}
                        analyticsLabel={
                            PlatformOverviewUserEvents.ViewDataCatalogFooterLinkClick
                        }
                        className={getButtonClassName({
                            variant: ButtonVariants.primaryOutline,
                            size: "md",
                            rounded: "md",
                        })}
                    >
                        View Data Catalog
                    </Link>
                    <Anchor
                        openInNewTab
                        href={RWA_URLS.docs}
                        analyticsLabel={
                            PlatformOverviewUserEvents.ViewDocumentationFooterLinkClick
                        }
                        className={getButtonClassName({
                            variant: ButtonVariants.primaryLink,
                            size: "md",
                        })}
                    >
                        Documentation
                    </Anchor>
                </div>
            </div>
        </section>
    );
}
